// src/pages/services/ProductDesignPage.jsx
import { motion } from "framer-motion";
import Lottie from "lottie-react";
import uiuxAnim from "../../assets/lottie/product.json";

export default function ProductDesignPage() {
  return (
    <div className="w-full bg-transparent text-black pt-28 px-6 md:px-20 pb-24">

      {/* ================= HERO ================= */}
      <section className="mx-auto flex max-w-7xl flex-col items-center gap-10 text-center">

        <motion.div
          initial={{ opacity: 0, y: 25 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
        >
          <h1 className="hero-heading mb-6">
            Product Design
          </h1>

          <p className="hero-copy">
            We design intuitive, user focused digital products grounded in
            research, strategy, and real world usability.
          </p>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="flex justify-center"
        >
          <Lottie
            animationData={uiuxAnim}
            loop
            style={{ width: "85%", maxWidth: "420px" }}
          />
        </motion.div>

      </section>

      {/* ================= WHAT WE DELIVER ================= */}
      <section className="mx-auto mt-28 max-w-6xl text-center">
        <h2 className="section-heading mb-10">
          What We Deliver
        </h2>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-10">
          {[
            "User research & product flows",
            "Wireframes & information structure",
            "High fidelity UI design",
            "Design systems & components",
            "Interactive prototypes",
            "Developer ready handoff",
          ].map((item, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="
                glass-card rounded-[22px] p-6
              "
            >
              <h3 className="text-[16px] font-medium">{item}</h3>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= PROCESS ================= */}
      <section className="mx-auto mt-32 max-w-6xl text-center">
        <h2 className="section-heading mb-10">
          Our Process
        </h2>

        <div className="space-y-14">
          {[
            ["01", "Research & Discovery", "Understanding users, goals, and problems."],
            ["02", "User Journeys", "Mapping flows and decision points."],
            ["03", "Wireframes", "Structuring the product experience."],
            ["04", "Visual Design", "Designing a clean, premium interface."],
            ["05", "Prototyping", "Interactive prototypes for validation."],
            ["06", "Handoff & Support", "Design systems and dev collaboration."],
          ].map(([step, title, desc], i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.08 }}
              className="glass-card flex gap-6 items-start rounded-[22px] p-5"
            >
              <h3 className="text-4xl font-semibold text-black">{step}</h3>
              <div>
                <h4 className="text-xl font-semibold mb-1">{title}</h4>
                <p className="text-black/60 text-[15px] leading-[1.6]">{desc}</p>
              </div>
            </motion.div>
          ))}
        </div>
      </section>

      {/* ================= WHAT WE NEED ================= */}
      <section className="mx-auto mt-32 max-w-6xl text-center">
        <h2 className="section-heading mb-6">
          What We Need From You
        </h2>

        <ul className="mx-auto max-w-3xl space-y-4 text-center text-[16px] text-neutral-700">
          <li>• Product idea & vision</li>
          <li>• Target users</li>
          <li>• Business goals</li>
          <li>• Brand direction or references</li>
        </ul>
      </section>

      {/* CTA */}
      <section className="mx-auto mt-32 max-w-6xl text-center">
        <h2 className="section-heading mb-6">
          Let’s design your product
        </h2>

        <a
          href="/contact"
          className="inline-block px-8 py-3 rounded-full bg-black text-white font-medium hover:bg-opacity-80 transition"
        >
          Contact Us
        </a>
      </section>

    </div>
  );
}
